import { NostoSectionComponent } from '@theme/nosto-section-base';

/**
 * <ax-search-collections-nosto> — collections personnalisées DANS la modal de
 * recherche (ce n'est pas une section en flow de page). Différences avec les autres
 * sections Nosto, exprimées en overrides de nosto-section-base.js :
 *   • PAS de gate viewport (`useViewportGate = false`) : la section vit dans un
 *     `<dialog>` fermé, où l'IntersectionObserver ne se déclenche jamais. Le fetch est
 *     ouvert par `renderGate()` : à l'OUVERTURE de la modale, préchauffée dès qu'un
 *     déclencheur de recherche est survolé ou reçoit le focus — donc le contenu est
 *     déjà là quand la modale s'ouvre, sans faire payer la requête aux pages vues où
 *     l'utilisateur n'ouvre jamais la recherche (l'immense majorité).
 *   • Placement repéré par son id global `#search-collection-list-1` (robuste : ne
 *     dépend pas de la résolution des refs du framework dans la modal).
 *   • Timeout handle plus long : Nosto injecte tard (~6,5 s) dans ce placement.
 *   • PAS de `nostoFallback` : sans handle, la section rend par défaut le métaobjet
 *     « home » curaté (le mockup collections Shopify n'est qu'un repli ultime côté Liquid).
 *   • Handle tardif : si Nosto injecte APRÈS le rendu fallback, on re-fetch en perso.
 */

const PLACEMENT_ID = 'search-collection-list-1';
const SECTION_ID = 'ax-search-collections-nosto';
const DEFAULT_HANDLE_TIMEOUT_MS = 8000;
const LATE_HANDLE_POLL_INTERVAL = 150;

/**
 * Déclencheurs d'ouverture de la modale. Les survoler / les focaliser est le signal
 * d'intention le plus précoce disponible : on préchauffe là, la modale s'ouvre alors
 * déjà remplie.
 */
const SEARCH_TRIGGER_SELECTOR = '[on\\:click="#search-modal/showDialog"]';

/** Plafond du préchauffage au repos : au-delà, on fetche même si le navigateur est occupé. */
const IDLE_WARMUP_TIMEOUT_MS = 2000;
/** Repli pour les navigateurs sans `requestIdleCallback` (Safari &lt; 17). */
const IDLE_WARMUP_FALLBACK_MS = 1200;

/** @returns {string | null} */
function readHandle() {
  const node = document.querySelector(`#${PLACEMENT_ID} [data-handle]`);
  return node?.getAttribute('data-handle')?.trim() || null;
}

class AxSearchCollectionsNostoComponent extends NostoSectionComponent {
  static elementName = SECTION_ID;

  /** @type {number | null} */
  #lateHandleInterval = null;

  get useViewportGate() {
    return false;
  }

  get handleTimeout() {
    return parseInt(this.dataset.nostoTimeout ?? '', 10) || DEFAULT_HANDLE_TIMEOUT_MS;
  }

  getPlacement() {
    return document.getElementById(PLACEMENT_ID);
  }

  /**
   * Porte d'entrée du fetch. Trois déclencheurs, le premier qui vient l'emporte :
   *
   *   1. le repos du navigateur APRÈS le `load` — le filet le plus large. La recherche
   *      est un point de passage majeur du trafic : on ne veut pas que le contenu
   *      dépende d'un survol qui n'aura peut-être jamais lieu (tactile, clavier, clic
   *      direct). Placé après `load` et dans un `requestIdleCallback`, ce fetch ne
   *      dispute rien au chemin critique — c'était tout l'objet du changement.
   *   2. l'intention de recherche (survol / focus d'un déclencheur) — pour le cas où
   *      l'utilisateur est plus rapide que le `load`.
   *   3. l'ouverture effective de la modale — dernier recours.
   *
   * En mode éditeur on rend tout de suite : le `<dialog>` n'y est pas ouvert par
   * l'utilisateur et la section doit rester inspectable.
   *
   * @returns {Promise<void> | null}
   */
  renderGate() {
    if (window.Shopify?.designMode) return null;

    const dialog = /** @type {HTMLDialogElement | null} */ (this.closest('dialog'));
    if (!dialog || dialog.open) return null;

    return new Promise((resolve) => {
      let done = false;
      /** @type {MutationObserver | null} */
      let observer = null;
      /** @type {(() => void) | null} */
      let removeWarmup = null;

      const open = () => {
        if (done) return;
        done = true;
        observer?.disconnect();
        removeWarmup?.();
        resolve();
      };

      observer = new MutationObserver(() => {
        if (dialog.open) open();
      });
      observer.observe(dialog, { attributes: true, attributeFilter: ['open'] });

      // 2) Intention de recherche.
      const triggers = document.querySelectorAll(SEARCH_TRIGGER_SELECTOR);
      if (triggers.length > 0) {
        const listeners = [...triggers];
        for (const trigger of listeners) {
          trigger.addEventListener('pointerenter', open, { once: true });
          trigger.addEventListener('focusin', open, { once: true });
        }
        removeWarmup = () => {
          for (const trigger of listeners) {
            trigger.removeEventListener('pointerenter', open);
            trigger.removeEventListener('focusin', open);
          }
        };
      }

      // 1) Préchauffage au repos, une fois la page chargée.
      const warmWhenIdle = () => {
        if (typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(open, { timeout: IDLE_WARMUP_TIMEOUT_MS });
          return;
        }
        setTimeout(open, IDLE_WARMUP_FALLBACK_MS);
      };

      if (document.readyState === 'complete') warmWhenIdle();
      else window.addEventListener('load', warmWhenIdle, { once: true });

      this.abortSignal?.addEventListener?.('abort', open, { once: true });
    });
  }

  resolveSectionId() {
    return SECTION_ID;
  }

  /**
   * Sans handle on ne force PAS `nostoFallback` : la section rend par défaut le
   * métaobjet « home » curaté.
   * @param {string | null} handle
   * @returns {URL}
   */
  buildSectionUrl(handle) {
    const url = new URL(window.location.href);
    if (handle) url.searchParams.set('handleMetaobject', handle);
    return url;
  }

  /** Repli rendu : on continue de guetter un handle tardif pour re-fetcher en perso. */
  onNoHandleRendered() {
    if (this.#lateHandleInterval != null) return;
    this.#lateHandleInterval = window.setInterval(() => {
      if (this.abortSignal.aborted) {
        this.#clearLateHandle();
        return;
      }
      const handle = readHandle();
      if (!handle) return;
      this.#clearLateHandle();
      this.fetchAndRender(handle).catch(() => {});
    }, LATE_HANDLE_POLL_INTERVAL);
  }

  #clearLateHandle() {
    if (this.#lateHandleInterval != null) {
      clearInterval(this.#lateHandleInterval);
      this.#lateHandleInterval = null;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#clearLateHandle();
  }
}

if (!customElements.get(SECTION_ID)) {
  customElements.define(SECTION_ID, AxSearchCollectionsNostoComponent);
}
